import * as React from "react"
import { ChevronDown, ChevronRight, Check, ChevronsUpDown, X, Loader2 } from "lucide-react"

import { cn } from "../../lib/utils"
import { Button } from "./button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover"
import { Input } from "./input"
import { Checkbox } from "./checkbox"

export interface TreeNode {
  title: React.ReactNode
  value: string
  key: string
  name: string
  disabled?: boolean
  children?: TreeNode[]
}

interface TreeSelectProps {
  treeData: TreeNode[]
  value?: string | string[]
  onChange?: (value: string | string[]) => void
  placeholder?: string
  showSearch?: boolean
  loading?: boolean
  disabled?: boolean
  className?: string
  multiple?: boolean
  filterTreeNode?: (inputValue: string, treeNode: TreeNode) => boolean
  showCheckedStrategy?: "SHOW_PARENT" | "SHOW_ALL"
}

interface TreeItemProps {
  node: TreeNode
  level: number
  selectedValues: Set<string>
  expandedKeys: Set<string>
  onSelect: (value: string, node: TreeNode) => void
  onToggleExpand: (key: string) => void
  multiple: boolean
  searchValue: string
  filterTreeNode?: (inputValue: string, treeNode: TreeNode) => boolean
}

const TreeItem: React.FC<TreeItemProps> = ({
  node,
  level,
  selectedValues,
  expandedKeys,
  onSelect,
  onToggleExpand,
  multiple,
  searchValue,
  filterTreeNode,
}) => {
  const hasChildren = node.children && node.children.length > 0
  const isExpanded = expandedKeys.has(node.key)
  const isSelected = selectedValues.has(node.value)

  // Filter logic
  const matchesFilter = filterTreeNode
    ? filterTreeNode(searchValue, node)
    : !searchValue || node.name.toLowerCase().includes(searchValue.toLowerCase())

  // Check if any children match the filter
  const hasMatchingChildren = node.children?.some(child => {
    if (filterTreeNode) {
      return filterTreeNode(searchValue, child) ||
        child.children?.some(grandChild => filterTreeNode(searchValue, grandChild))
    }
    return child.name.toLowerCase().includes(searchValue.toLowerCase()) ||
      child.children?.some(grandChild => grandChild.name.toLowerCase().includes(searchValue.toLowerCase()))
  })

  // Hide if doesn't match and no children match
  if (searchValue && !matchesFilter && !hasMatchingChildren) {
    return null
  }

  // Auto-expand when searching
  const shouldExpand = searchValue ? (hasMatchingChildren || matchesFilter) : isExpanded

  return (
    <div>
      <div
        className={cn(
          "flex items-center gap-1 py-1 px-2 rounded-sm cursor-pointer hover:bg-muted",
          isSelected && !multiple && "bg-muted",
          node.disabled && "opacity-50 cursor-not-allowed"
        )}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={() => {
          if (node.disabled) return
          if (hasChildren && !multiple) {
            onToggleExpand(node.key)
          }
          if (!node.disabled) {
            onSelect(node.value, node)
          }
        }}
      >
        {hasChildren ? (
          <button
            type="button"
            className="p-0.5 hover:bg-muted rounded"
            onClick={(e) => {
              e.stopPropagation()
              onToggleExpand(node.key)
            }}
          >
            {shouldExpand ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        ) : (
          <span className="w-5" />
        )}

        {multiple ? (
          <Checkbox
            checked={isSelected}
            disabled={node.disabled}
            onCheckedChange={() => !node.disabled && onSelect(node.value, node)}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          !hasChildren && isSelected && <Check className="h-4 w-4 text-primary" />
        )}

        <span className="flex-1 text-sm truncate">{node.title}</span>
      </div>

      {hasChildren && shouldExpand && (
        <div>
          {node.children!.map((child) => (
            <TreeItem
              key={child.key}
              node={child}
              level={level + 1}
              selectedValues={selectedValues}
              expandedKeys={expandedKeys}
              onSelect={onSelect}
              onToggleExpand={onToggleExpand}
              multiple={multiple}
              searchValue={searchValue}
              filterTreeNode={filterTreeNode}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export function TreeSelect({
  treeData,
  value,
  onChange,
  placeholder = "Select...",
  showSearch = false,
  loading = false,
  disabled = false,
  className,
  multiple = false,
  filterTreeNode,
}: TreeSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [searchValue, setSearchValue] = React.useState("")
  const [expandedKeys, setExpandedKeys] = React.useState<Set<string>>(new Set())

  // Convert value to Set for easy lookup
  const selectedValues = React.useMemo(() => {
    if (!value) return new Set<string>()
    if (Array.isArray(value)) return new Set(value)
    return new Set([value])
  }, [value])

  // Find selected node labels
  const findNodeLabel = (nodes: TreeNode[], targetValue: string): React.ReactNode | null => {
    for (const node of nodes) {
      if (node.value === targetValue) return node.title
      if (node.children) {
        const found = findNodeLabel(node.children, targetValue)
        if (found) return found
      }
    }
    return null
  }

  const selectedLabels = React.useMemo(() => {
    const labels: React.ReactNode[] = []
    selectedValues.forEach((val) => {
      const label = findNodeLabel(treeData, val)
      if (label) labels.push(label)
    })
    return labels
  }, [selectedValues, treeData])

  const handleSelect = (nodeValue: string, node: TreeNode) => {
    if (multiple) {
      const newValues = new Set(selectedValues)
      if (newValues.has(nodeValue)) {
        newValues.delete(nodeValue)
      } else {
        newValues.add(nodeValue)
      }
      onChange?.(Array.from(newValues))
    } else {
      onChange?.(nodeValue)
      setOpen(false)
    }
  }

  const handleToggleExpand = (key: string) => {
    const newExpanded = new Set(expandedKeys)
    if (newExpanded.has(key)) {
      newExpanded.delete(key)
    } else {
      newExpanded.add(key)
    }
    setExpandedKeys(newExpanded)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange?.(multiple ? [] : "")
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal h-auto min-h-8",
            !selectedLabels.length && "text-muted-foreground",
            className
          )}
          disabled={disabled || loading}
        >
          <span className="flex-1 text-left truncate">
            {loading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading...
              </span>
            ) : selectedLabels.length > 0 ? (
              multiple ? (
                <span className="flex flex-wrap gap-1">
                  {selectedLabels.slice(0, 3).map((label, i) => (
                    <span key={i} className="bg-muted px-2 py-0.5 rounded text-xs">
                      {label}
                    </span>
                  ))}
                  {selectedLabels.length > 3 && (
                    <span className="text-muted-foreground text-xs">+{selectedLabels.length - 3} more</span>
                  )}
                </span>
              ) : (
                selectedLabels[0]
              )
            ) : (
              placeholder
            )}
          </span>
          <span className="flex items-center gap-1 ml-2">
            {selectedLabels.length > 0 && !loading && (
              <X
                className="h-4 w-4 shrink-0 opacity-50 hover:opacity-100"
                onClick={handleClear}
              />
            )}
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="flex flex-col">
          {showSearch && (
            <div className="p-2 border-b">
              <Input
                placeholder="Search..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="h-8"
              />
            </div>
          )}
          <div className="max-h-[300px] overflow-y-auto p-1">
            {treeData.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">No data</div>
            ) : (
              treeData.map((node) => (
                <TreeItem
                  key={node.key}
                  node={node}
                  level={0}
                  selectedValues={selectedValues}
                  expandedKeys={expandedKeys}
                  onSelect={handleSelect}
                  onToggleExpand={handleToggleExpand}
                  multiple={multiple}
                  searchValue={searchValue}
                  filterTreeNode={filterTreeNode}
                />
              ))
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
